import React, { useState } from 'react';
import { siteConfig } from '../data/config';
import { useAnalytics } from '../hooks/useAnalytics';

function Contact() {
  const { trackFormSubmission } = useAnalytics();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  // Validation rules
  const validationRules = {
    name: {
      required: true,
      minLength: 2,
      maxLength: 50,
      pattern: /^[a-zA-Z\s]+$/
    },
    email: {
      required: true,
      pattern: /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    },
    subject: {
      required: true
    },
    message: {
      required: true,
      minLength: 10,
      maxLength: 1000
    }
  };

  // Validation functions
  const validateField = (name, value) => {
    const rules = validationRules[name];
    const fieldErrors = [];

    if (rules.required && !value.trim()) {
      fieldErrors.push('This field is required');
    }

    if (rules.minLength && value.length < rules.minLength) {
      fieldErrors.push(`Must be at least ${rules.minLength} characters`);
    }

    if (rules.maxLength && value.length > rules.maxLength) {
      fieldErrors.push(`Must be no more than ${rules.maxLength} characters`);
    }

    if (rules.pattern && !rules.pattern.test(value)) {
      if (name === 'email') {
        fieldErrors.push('Please enter a valid email address');
      } else if (name === 'name') {
        fieldErrors.push('Name can only contain letters and spaces');
      }
    }

    return fieldErrors;
  };

  const validateForm = () => {
    const newErrors = {};
    Object.keys(formData).forEach(field => {
      const fieldErrors = validateField(field, formData[field]);
      if (fieldErrors.length > 0) {
        newErrors[field] = fieldErrors;
      }
    });
    return newErrors;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Real-time validation for touched fields
    if (touched[name]) {
      const fieldErrors = validateField(name, value);
      setErrors(prev => {
        const newErrors = { ...prev };
        if (fieldErrors.length > 0) {
          newErrors[name] = fieldErrors;
        } else {
          delete newErrors[name];
        }
        return newErrors;
      });
    }
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    setTouched(prev => ({
      ...prev,
      [name]: true
    }));

    const fieldErrors = validateField(name, value);
    setErrors(prev => {
      const newErrors = { ...prev };
      if (fieldErrors.length > 0) {
        newErrors[name] = fieldErrors;
      } else {
        delete newErrors[name];
      }
      return newErrors;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Mark all fields as touched
    const allTouched = Object.keys(formData).reduce((acc, field) => {
      acc[field] = true;
      return acc;
    }, {});
    setTouched(allTouched);

    // Validate all fields
    const newErrors = validateForm();
    setErrors(newErrors);

    // If there are errors, don't submit
    if (Object.keys(newErrors).length > 0) {
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const response = await fetch(siteConfig.formspreeEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        setSubmitStatus('success');
        trackFormSubmission('contact_form');
        setFormData({
          name: '',
          email: '',
          subject: '',
          message: ''
        });
        setErrors({});
        setTouched({});
      } else {
        console.error('Formspree error:', response.status, response.statusText);
        setSubmitStatus('error');
      }
    } catch (error) {
      console.error('Network error:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const getFieldClassName = (fieldName) => {
    const baseClass = 'form-input';
    if (touched[fieldName] && errors[fieldName]) {
      return `${baseClass} error`;
    }
    if (touched[fieldName] && !errors[fieldName] && formData[fieldName]) {
      return `${baseClass} valid`;
    }
    return baseClass;
  };

  return (
    <div className="contact-page">
      <div className="contact-container">
        <h1>Contact Me</h1>
        <p>Have questions about my apps? Send me a message!</p>
        
        <form onSubmit={handleSubmit} className="contact-form">
          <div className="form-group">
            <label htmlFor="name">Name *</label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              onBlur={handleBlur}
              className={getFieldClassName('name')}
              placeholder="Enter your full name"
            />
            {touched.name && errors.name && (
              <div className="error-message">
                {errors.name.map((error, index) => (
                  <span key={index}>{error}</span>
                ))}
              </div>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="email">Email *</label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              onBlur={handleBlur}
              className={getFieldClassName('email')}
              placeholder="Enter your email address"
            />
            {touched.email && errors.email && (
              <div className="error-message">
                {errors.email.map((error, index) => (
                  <span key={index}>{error}</span>
                ))}
              </div>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="subject">Subject *</label>
            <select
              id="subject"
              name="subject"
              value={formData.subject}
              onChange={handleChange}
              onBlur={handleBlur}
              className={getFieldClassName('subject')}
            >
              <option value="">Select a subject</option>
              <option value="App Support">App Support</option>
              <option value="Feature Request">Feature Request</option>
              <option value="Bug Report">Bug Report</option>
              <option value="General Question">General Question</option>
            </select>
            {touched.subject && errors.subject && (
              <div className="error-message">
                {errors.subject.map((error, index) => (
                  <span key={index}>{error}</span>
                ))}
              </div>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="message">Message *</label>
            <textarea
              id="message"
              name="message"
              value={formData.message}
              onChange={handleChange}
              onBlur={handleBlur}
              className={getFieldClassName('message')}
              placeholder="Please describe your question, bug report, or feature request..."
              rows="6"
            />
            {touched.message && errors.message && (
              <div className="error-message">
                {errors.message.map((error, index) => (
                  <span key={index}>{error}</span>
                ))}
              </div>
            )}
            <div className="character-count">
              {formData.message.length}/1000 characters
            </div>
          </div>

          <button 
            type="submit" 
            disabled={isSubmitting || Object.keys(errors).length > 0}
            className="submit-btn"
          >
            {isSubmitting ? 'Sending...' : 'Send Message'}
          </button>

          {submitStatus === 'success' && (
            <div className="success-message">
              <p>Thank you! Your message has been sent successfully.</p>
            </div>
          )}

          {submitStatus === 'error' && (
            <div className="error-message">
              <p>Sorry, there was an error sending your message. Please try again.</p>
            </div>
          )}
        </form>
      </div>
    </div>
  );
}

export default Contact;